/**
 * RoundsListScreen — route `/rounds` (operator+).
 *
 * Lists every round via `listRounds` (GET /v1/rounds → {rounds: RoundListItem[]})
 * so an operator can pick one to drill into. Each row links to `/rounds/:roundId`
 * (the round dashboard). Replaces the old hardcoded DEFAULT_ROUND.
 *
 * Role: the route is already guarded by RequireRole "operator" in App.tsx, so
 * this screen renders unconditionally for whoever reaches it.
 *
 * DEMO mode (no VITE_API_BASE_URL): no real API exists, so we serve a small
 * synthetic list instead of hitting the network. The global DEMO badge in the
 * AppShell already makes the demo state visible.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import type { ChipProps } from "@mui/material";
import ListAltIcon from "@mui/icons-material/ListAlt";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutlineOutlined";

import { api, useApiQuery, isApiError } from "../api/useApi";
import { IS_DEMO } from "../api/client";
import type { RoundListItem, RoundListResponse } from "../api/client";
import { Loading } from "../components/Loading";
import { ProblemAlert } from "../components/ProblemAlert";
import { EmptyState } from "../components/EmptyState";

// ── Status presentation (RoundListState: queued|running|completed|failed|empty) ─
type RoundStatus = RoundListItem["status"];

const STATUS_META: Record<
  RoundStatus,
  { label: string; color: ChipProps["color"] }
> = {
  queued: { label: "En cola", color: "info" },
  running: { label: "En proceso", color: "secondary" },
  completed: { label: "Completada", color: "success" },
  failed: { label: "Con errores", color: "error" },
  closed: { label: "Cerrada", color: "default" },
  empty: { label: "Sin documentos", color: "warning" },
};

function StatusChip({ status }: { status: RoundStatus }) {
  // Defensive: a future/unknown wire value still renders a readable chip.
  const meta = STATUS_META[status] ?? {
    label: status,
    color: "default" as const,
  };
  return (
    <Chip
      size="small"
      label={meta.label}
      color={meta.color}
      variant="outlined"
    />
  );
}

const DATE_FMT = new Intl.DateTimeFormat("es-MX", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatGeneratedAt(value: string | null): string {
  if (!value) return "—";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : DATE_FMT.format(d);
}

function formatTotal(total: number | null): string {
  return total == null ? "—" : new Intl.NumberFormat("es-MX").format(total);
}

// ── DEMO data (used only when no API base URL is configured) ──────────────────
const DEMO_RESPONSE: RoundListResponse = {
  rounds: [
    {
      round_id: "ronda-2026-06-demo",
      status: "completed",
      total: 1280,
      generated_at: "2026-06-08T18:42:00Z",
    },
    {
      round_id: "ronda-2026-05-demo",
      status: "running",
      total: 540,
      generated_at: "2026-06-09T09:15:00Z",
    },
    {
      round_id: "ronda-2026-04-demo",
      status: "failed",
      total: 96,
      generated_at: "2026-05-30T11:02:00Z",
    },
    {
      round_id: "ronda-borrador-demo",
      status: "empty",
      total: null,
      generated_at: null,
    },
  ],
};

async function loadRounds(): Promise<RoundListResponse> {
  if (IS_DEMO) return DEMO_RESPONSE;
  const res = await api.GET("/rounds", {});
  return res.data!; // RoundListResponse — non-null on 2xx (client throws otherwise)
}

function nowRoundId(): string {
  return new Date()
    .toISOString()
    .slice(0, 16) // "YYYY-MM-DDTHH:MM"
    .replace("T", "T")
    .replace(":", "-")
    .replace(":", "-");
}

export default function RoundsListScreen() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // ── Nueva ronda dialog ───────────────────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [newRoundId, setNewRoundId] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<Error | null>(null);

  function openCreateDialog() {
    setNewRoundId(nowRoundId());
    setNewDisplayName("");
    setCreateError(null);
    setCreateOpen(true);
  }

  async function handleCreateRound() {
    const id = newRoundId.trim();
    if (!id) return;
    setCreating(true);
    setCreateError(null);
    try {
      if (!IS_DEMO) {
        await api.POST("/rounds", {
          body: {
            round_id: id,
            display_name: newDisplayName.trim() || null,
          },
        });
      }
      setCreateOpen(false);
      navigate(`/rounds/${encodeURIComponent(id)}/operations`);
    } catch (err: unknown) {
      if (isApiError(err)) setCreateError(err);
      else setCreateError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setCreating(false);
    }
  }

  const { data, error, loading, reload } = useApiQuery<RoundListResponse>(
    loadRounds,
    [],
  );

  const goToRound = (roundId: string) =>
    navigate(`/rounds/${encodeURIComponent(roundId)}`);

  return (
    <Box>
      <Stack
        direction="row"
        spacing={2}
        sx={{ mb: 2, alignItems: "center", justifyContent: "space-between" }}
      >
        <Box>
          <Typography variant="h1">Rondas</Typography>
          <Typography variant="body2" color="text.secondary">
            Selecciona una ronda para ver su tablero, cola de revisión y
            operaciones.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddCircleOutlineIcon />}
          onClick={openCreateDialog}
        >
          Nueva ronda
        </Button>
      </Stack>

      {/* Non-auth errors only; 401/403 are handled by the route guards. */}
      {error && <ProblemAlert error={error} onRetry={reload} />}

      {loading && <Loading label="Cargando rondas…" />}

      {!loading && !error && data && data.rounds.length === 0 && (
        <EmptyState
          icon={<ListAltIcon fontSize="inherit" />}
          title="No hay rondas"
          description="Crea una ronda nueva para empezar a cargar expedientes."
          action={
            <Button
              variant="contained"
              startIcon={<AddCircleOutlineIcon />}
              onClick={openCreateDialog}
            >
              Nueva ronda
            </Button>
          }
        />
      )}

      {!loading &&
        !error &&
        data &&
        data.rounds.length > 0 &&
        (isMobile ? (
          <Stack spacing={1.5}>
            {data.rounds.map((round) => (
              <Card key={round.round_id} variant="outlined">
                <CardActionArea
                  onClick={() => goToRound(round.round_id)}
                  sx={{ p: 2 }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      {round.display_name && (
                        <Typography variant="h3">
                          {round.display_name}
                        </Typography>
                      )}
                      <Typography
                        variant={round.display_name ? "caption" : "h3"}
                        color={
                          round.display_name ? "text.secondary" : "text.primary"
                        }
                        sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                      >
                        {round.round_id}
                      </Typography>
                    </Box>
                    <StatusChip status={round.status} />
                  </Stack>
                  <Divider sx={{ my: 1.5 }} />
                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      color: "text.secondary",
                      justifyContent: "space-between",
                    }}
                  >
                    <Box>
                      <Typography variant="caption" sx={{ display: "block" }}>
                        Expedientes
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {formatTotal(round.total)}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography variant="caption" sx={{ display: "block" }}>
                        Generada
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {formatGeneratedAt(round.generated_at)}
                      </Typography>
                    </Box>
                  </Stack>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        ) : (
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Lista de rondas">
              <TableHead>
                <TableRow>
                  <TableCell>Ronda</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Expedientes</TableCell>
                  <TableCell>Generada</TableCell>
                  <TableCell padding="checkbox" aria-hidden />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.rounds.map((round) => (
                  <TableRow
                    key={round.round_id}
                    hover
                    onClick={() => goToRound(round.round_id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      {round.display_name && (
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {round.display_name}
                        </Typography>
                      )}
                      <Typography
                        variant={round.display_name ? "caption" : "body2"}
                        color={
                          round.display_name ? "text.secondary" : "text.primary"
                        }
                        sx={{ fontFamily: "monospace" }}
                      >
                        {round.round_id}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={round.status} />
                    </TableCell>
                    <TableCell align="right">
                      {formatTotal(round.total)}
                    </TableCell>
                    <TableCell>
                      {formatGeneratedAt(round.generated_at)}
                    </TableCell>
                    <TableCell padding="checkbox">
                      <ChevronRightIcon color="action" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ))}

      {/* Dialog: crear nueva ronda */}
      <Dialog
        open={createOpen}
        onClose={() => !creating && setCreateOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Nueva ronda</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            El ID se pre-llena con la fecha y hora actual. Puedes cambiarlo o
            añadir un nombre descriptivo para ubicarla fácilmente.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              label="ID de la ronda"
              value={newRoundId}
              onChange={(e) => setNewRoundId(e.target.value)}
              size="small"
              required
              fullWidth
              disabled={creating}
              helperText="Identificador único e inmutable."
            />
            <TextField
              label="Nombre descriptivo"
              placeholder="Ej. Junio 2026"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              size="small"
              fullWidth
              disabled={creating}
              helperText="Opcional. Se puede cambiar después."
            />
            {createError && (
              <Typography variant="caption" color="error">
                {createError.message}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateRound}
            disabled={!newRoundId.trim() || creating}
            autoFocus
          >
            {creating ? "Creando…" : "Crear e ir a Operaciones"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
